/* Shared Web Push helpers used by Navbar (auto-subscribe + first-login modal)
   and ShareAccessCode Settings (manual toggle). */

const API_BASE_URL = 'https://api.omegavoid.com';

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  typeof Notification !== 'undefined';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
};

const registerSw = async () => {
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  return reg;
};

const fetchVapidKey = async () => {
  const resp = await fetch(`${API_BASE_URL}/push/public-key`);
  if (!resp.ok) throw new Error('Could not load VAPID key');
  const { public_key } = await resp.json();
  return public_key;
};

const postSubscription = async (authToken, sub) => {
  const j = sub.toJSON();
  const resp = await fetch(`${API_BASE_URL}/push/subscribe`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ endpoint: j.endpoint, keys: j.keys })
  });
  if (!resp.ok) throw new Error('Subscription rejected by server');
};

/**
 * Full opt-in flow: prompt for permission, register SW, subscribe, POST to backend.
 * Used by the Settings toggle and the first-login modal Enable button.
 */
export const enablePushNotifications = async (authToken) => {
  if (!isPushSupported()) throw new Error('Not supported on this browser');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Notification permission denied');
  const reg = await registerSw();
  const vapidKey = await fetchVapidKey();
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey)
  });
  await postSubscription(authToken, sub);
};

/**
 * Silent auto-subscribe: if permission is already granted and no subscription
 * exists yet on this browser, subscribe without prompting. Safe to call on
 * every login — no-op if already subscribed or permission not granted.
 */
export const ensurePushSubscribed = async (authToken) => {
  if (!isPushSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  try {
    const reg = await registerSw();
    const existing = await reg.pushManager.getSubscription();
    if (existing) return true;
    const vapidKey = await fetchVapidKey();
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey)
    });
    await postSubscription(authToken, sub);
    return true;
  } catch (e) {
    console.debug('Auto-subscribe skipped:', e && e.message);
    return false;
  }
};

export const disablePushNotifications = async (authToken) => {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  const sub = reg && (await reg.pushManager.getSubscription());
  const endpoint = sub && sub.endpoint;
  if (sub) await sub.unsubscribe();
  if (endpoint) {
    await fetch(`${API_BASE_URL}/push/unsubscribe`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ endpoint })
    });
  }
};
